/**
 * File-drop handling for the whole thread view, not just the composer.
 *
 * Ported from t3code, which attaches these to the entire `ChatView` while Forge
 * had the equivalent logic inlined in `ChatComposer` and bound to the composer
 * box alone. The behaviour was already identical — same `types.includes("Files")`
 * guard, same `relatedTarget`/`contains` re-entry check, same `copy` drop
 * effect — so what this changes is the size of the target: dropping a
 * screenshot anywhere in the thread attaches it, instead of only onto the
 * ~100px-tall composer.
 *
 * Structural typing rather than `React.DragEvent` so the logic is testable
 * without a DOM: every branch here is a guard, and guards are exactly what
 * silently stops firing.
 */
export interface WorkspaceFileDragEvent {
  readonly dataTransfer: {
    readonly types: ReadonlyArray<string>;
    readonly files: Iterable<File>;
    dropEffect: string;
  };
  readonly relatedTarget: EventTarget | null;
  readonly currentTarget: {
    contains(target: Node | null): boolean;
  };
  preventDefault(): void;
}

export interface WorkspaceFileDropHost {
  setDragActive(active: boolean): void;
  addFiles(files: File[]): void;
}

function isFileDrag(event: WorkspaceFileDragEvent): boolean {
  return event.dataTransfer.types.includes("Files");
}

/**
 * Dragging across a child element fires `dragleave` on the parent. Without this
 * the affordance flickers off every time the pointer crosses an internal
 * boundary — which, over a whole thread view, is constantly.
 */
function movedWithinDropTarget(event: WorkspaceFileDragEvent): boolean {
  return event.relatedTarget !== null && event.currentTarget.contains(event.relatedTarget as Node);
}

export function makeWorkspaceFileDropHandlers(host: WorkspaceFileDropHost) {
  return {
    onDragEnter(event: WorkspaceFileDragEvent) {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      if (movedWithinDropTarget(event)) return;
      host.setDragActive(true);
    },
    onDragOver(event: WorkspaceFileDragEvent) {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      host.setDragActive(true);
    },
    onDragLeave(event: WorkspaceFileDragEvent) {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      if (movedWithinDropTarget(event)) return;
      host.setDragActive(false);
    },
    onDrop(event: WorkspaceFileDragEvent) {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      host.setDragActive(false);
      host.addFiles(Array.from(event.dataTransfer.files));
    },
  };
}
